<template>
  <section>
    <div class="popups">
      <b-sidebar
        v-model="showFilters"
        right
        shadow
        backdrop
        bg-variant="white"
        no-header
      >
        <template>
          <div class="container sidebar-header">
            <b-row>
              <div class="col">
                <b-button
                  variant="primary"
                  @click="
                    applyFilters();
                    loadItems();
                    showFilters = false;
                  "
                  >Apply</b-button
                >
              </div>

              <div class="col-auto text-right">
                <b-button
                  type="reset"
                  variant="outline-danger"
                  @click="resetFormFilters()"
                >
                  Reset
                </b-button>
                <b-button
                  variant="outline-secondary"
                  class="ml-1"
                  @click="
                    showFilters = false;
                    formFilters = { ...itemsFilters };
                  "
                >
                  Cancel
                </b-button>
              </div>
            </b-row>
          </div>

          <div class="px-3 py-2">
            <b-form-group label="Search:">
              <b-form-input v-model="formFilters.search" />
            </b-form-group>

            <b-form-group label="Brand:">
              <b-v-select
                v-model="formFilters.brandId"
                :searchable="true"
                :options="brandsFilter"
              />
            </b-form-group>

            <b-form-group label="Type:">
              <b-v-select
                v-model="formFilters.type"
                :searchable="true"
                :options="typeOptions"
              />
            </b-form-group>

            <b-form-group label="Ownership:">
              <b-v-select
                v-model="formFilters.ownership"
                :searchable="true"
                :options="ownershipOptions"
              />
            </b-form-group>
          </div>
        </template>
      </b-sidebar>
    </div>

    <div class="header">
      <div class="header-body">
        <b-row class="align-items-end">
          <div class="col">
            <h1 class="header-title">
              Import Sheet Mapping Methods
            </h1>
            <ActiveFiltersCard :itemsFilters="itemsFilters" :resetFilterWithKey="resetFilterWithKey" :loadItems="loadItems">
              <template v-slot:filterVal(brandId)="data">
                <span
                  v-if="myBrands && myBrands.find(a => a.id == data.item)"
                  >{{ myBrands.find(a => a.id == data.item).name }}</span
                >
                <span v-else>-</span>
              </template>
              <template v-slot:filterVal(type)="data">
                <span>{{ getTypeLabel(data.item) }}</span>
              </template>
              <template v-slot:filterVal(ownership)="data">
                <span>{{ getOwnershipLabel(data.item) }}</span>
              </template>
            </ActiveFiltersCard>
          </div>
          <div class="col-auto">
            <router-link
              :to="{ name: 'Transfers.ImportSheetMappingMethods.Create' }"
              class="btn btn-success mr-2"
            >
              <i class="mr-2 fa fa-plus" />
              Add New
            </router-link>
            <b-button variant="primary" @click="showFilters = !showFilters">
              <i class="fa fa-filter mr-2"></i>
              Filters
            </b-button>
          </div>
        </b-row>
      </div>
    </div>

    <TableListing
      class="mb-0"
      :loading-page="loadingItems"
      :failed-loading="failedLoadItems"
      head-variant="dark"
      :items.sync="items"
      :fields.sync="fields"
      :pagination.sync="pagination"
      :options.sync="tableOptions"
      v-on:pagination-changed="loadItems"
    >
      <template v-slot:cell(id)="data">
        <div class="d-flex">
          <a
            v-if="data.item.exampleFileUrl"
            :href="data.item.exampleFileUrl"
            target="_blank"
            class="btn btn-outline-primary btn-sm mr-2"
            title="Download Example"
          >
            <i class="fa fa-download"></i>
          </a>
          <b-button variant="secondary"
            :to="{
              name: 'Transfers.ImportSheetMappingMethods.Edit',
              params: { id: data.item.id }
            }"
          >
            Edit
          </b-button>
          <b-button
            variant="outline-secondary"
            class="ml-2"
            :disabled="cloningId === data.item.id"
            title="Clone"
            @click="cloneItem(data.item)"
          >
            <i class="fa fa-copy mr-1"></i>
            Clone
          </b-button>
        </div>
      </template>

      <template v-slot:cell(brandId)="data">
        <span
          v-if="myBrands && myBrands.find(a => a.id == data.item.brandId)"
          >{{ myBrands.find(a => a.id == data.item.brandId).name }}</span
        >
        <span v-else>-</span>
      </template>

      <template v-slot:cell(type)="data">
        <span>{{ getTypeLabel(data.item.type) }}</span>
      </template>

      <template v-slot:cell(ownership)="data">
        <span>{{ getOwnershipLabel(data.item.ownership) }}</span>
      </template>

      <template v-slot:cell(createDateTimeUtc)="data">
        <span
          v-if="
            data.item.createDateTimeUtc &&
              !data.item.createDateTimeUtc.endsWith('Z')
          "
          >{{
            (data.item.createDateTimeUtc + 'Z')
              | moment('MMM Do YY, h:mm a')
          }}</span
        >
        <span v-else-if="data.item.createDateTimeUtc">{{
          data.item.createDateTimeUtc | moment('MMM Do YY, h:mm a')
        }}</span>
      </template>

      <template v-slot:cell(mappingsCount)="data">
        <span>{{ data.item.importSheetMappings ? data.item.importSheetMappings.length : 0 }}</span>
      </template>
    </TableListing>
  </section>
</template>

<script>
import { TableListing, ActiveFiltersCard } from '@/views/components/custom';
import { crudMixinGen, myCommonDataMixin } from '@/helpers/utils/vueMixins.js';

export default {
  name: 'Transfers.ImportSheetMappingMethods.Index',
  mixins: [crudMixinGen('importSheetMappingMethods'), myCommonDataMixin],
  components: {
    TableListing,
    ActiveFiltersCard
  },

  computed: {
    typeOptions() {
      return [
        { value: null, text: '- All Types -' },
        { value: 'ManualShipmentImport', text: 'Manual Shipment Import' },
        { value: 'StorefrontOrderImport', text: 'Storefront Order Import' }
      ];
    },
    ownershipOptions() {
      return [
        { value: null, text: '- All Ownership -' },
        { value: 'Everyone', text: 'Everyone' },
        { value: 'ClientOwned', text: 'Client Owned' }
      ];
    }
  },

  data: () => ({
    cloningId: null,
    fields: [
      { key: 'name', label: 'Name', sortable: false },
      { key: 'brandId', label: 'Brand', sortable: false },
      { key: 'type', label: 'Type', sortable: false },
      { key: 'ownership', label: 'Ownership', sortable: false },
      { key: 'mappingsCount', label: 'Mappings', sortable: false },
      { key: 'createDateTimeUtc', label: 'Created', sortable: false },
      { key: 'id', label: 'Action', sortable: false }
    ]
  }),

  mounted() {
    this.loadItems();
  },

  methods: {
    getTypeLabel(type) {
      const option = this.typeOptions.find(opt => opt.value === type);
      return option ? option.text : type;
    },
    getOwnershipLabel(ownership) {
      const option = this.ownershipOptions.find(opt => opt.value === ownership);
      return option ? option.text : ownership;
    },
    cloneItem(item) {
      this.$swal({
        title: 'Clone this mapping method?',
        text: `A copy of "${item.name}" will be created.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, clone it!'
      }).then(result => {
        if (!result.value) {
          return;
        }

        this.cloningId = item.id;
        this.$http
          .post('/api/common/import-sheet-mapping-methods/clone/' + item.id)
          .then(response => {
            const cloned = response.data && (response.data.data || response.data);
            const clonedName = (cloned && cloned.name) || `${item.name} - Clone`;
            this.$swal('Cloned!', `"${clonedName}" has been created.`, 'success');
            this.loadItems();
          })
          .catch(error => {
            console.error('Error cloning mapping method:', error);
            const msg =
              (error.response &&
                error.response.data &&
                error.response.data.errorMessage) ||
              'Failed to clone the mapping method.';
            this.$swal('Error', msg, 'error');
          })
          .finally(() => {
            this.cloningId = null;
          });
      });
    }
  }
};
</script>